import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@utils/api';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      loading: false,

      // Login using backend API (with Remember Me support)
      login: async (email, password, rememberMe = false) => {
        set({ loading: true });
        try {
          const response = await fetch(api('/api/auth/login'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          const data = await response.json();

          if (response.ok && data.success) {
            const { token, user } = data;
            
            // Clear old VIP cache before login to prevent showing wrong user's VIP status
            localStorage.removeItem('vip-status-cache');
            
            // Save to Zustand persist
            set({
              user,
              token,
              isAuthenticated: true,
              loading: false,
            });

            // If "Remember Me" is checked, also save to localStorage with expiry
            if (rememberMe) {
              const rememberData = {
                token,
                user,
                expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30 days
              };
              localStorage.setItem('rememberMe', JSON.stringify(rememberData));
            } else {
              localStorage.removeItem('rememberMe');
            }

            return { success: true };
          }

          set({ loading: false });
          return { success: false, message: data.message || 'Login failed' };
        } catch (error) {
          set({ loading: false });
          return {
            success: false,
            message: error.message || 'Login failed',
          };
        }
      },

      // Logout (clear Remember Me and VIP cache too)
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('vip-status-cache'); // ✅ Clear VIP cache to prevent status showing after logout
      },

      // Check authentication status (with Remember Me support)
      checkAuth: async () => {
        let token = get().token;
        
        // If no token in Zustand, check "Remember Me" in localStorage
        if (!token) {
          const rememberData = localStorage.getItem('rememberMe');
          if (rememberData) {
            try {
              const parsed = JSON.parse(rememberData);
              
              // Check if not expired
              if (parsed.expiresAt && Date.now() < parsed.expiresAt) {
                set({
                  user: parsed.user,
                  token: parsed.token,
                  isAuthenticated: true,
                });
                token = parsed.token;
              } else {
                localStorage.removeItem('rememberMe');
                localStorage.removeItem('vip-status-cache');
                set({ isAuthenticated: false });
                return;
              }
            } catch (err) {
              console.error('[Auth] Failed to parse Remember Me data');
              localStorage.removeItem('rememberMe');
              localStorage.removeItem('vip-status-cache');
              set({ isAuthenticated: false });
              return;
            }
          } else {
            set({ isAuthenticated: false });
            return;
          }
        }

        // Verify token with backend
        try {
          const response = await fetch(api('/api/user/me'), {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              set({
                user: data.user,
                isAuthenticated: true,
              });
            } else {
              set({
                user: null,
                token: null,
                isAuthenticated: false,
              });
              localStorage.removeItem('rememberMe');
              localStorage.removeItem('vip-status-cache');
            }
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
            });
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('vip-status-cache');
          }
        } catch (error) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('vip-status-cache');
        }
      },

      // Register new user
      register: async (email, password, username) => {
        set({ loading: true });
        try {
          const response = await fetch(api('/api/auth/register'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password, username }),
          });

          const data = await response.json();

          if (response.ok && data.success) {
            const { token, user } = data;
            
            // Clear old VIP cache before register to prevent showing wrong user's VIP status
            localStorage.removeItem('vip-status-cache');
            
            set({
              user,
              token,
              isAuthenticated: true,
              loading: false,
            });
            return { success: true };
          }

          set({ loading: false });
          return { success: false, message: data.message || 'Registration failed' };
        } catch (error) {
          set({ loading: false });
          return {
            success: false,
            message: error.message || 'Registration failed',
          };
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
